import { FC, useMemo } from 'react';

import { useTour } from '@reactour/tour';
import DOMPurify from 'dompurify';
import { dayjs, payloadDayjs } from 'utils/providers/Timezone';
import { Formik, type FormikHelpers } from 'formik';
import { TbDotsVertical } from 'react-icons/tb';
import { IoVideocam } from 'react-icons/io5';
import { compact } from 'lodash';
import { MdContentCopy } from 'react-icons/md';
import { useGetSubscription } from 'modules/account/settings/hooks';
import {
  useDeleteEvent,
  useGetEvent,
  useUpdateEvent,
} from 'modules/account/calendar/hooks';
import {
  EVENT_FORM,
  EVENT_SCHEMA,
} from 'modules/account/calendar/forms/schema';
import EventForm from 'modules/account/calendar/forms/EventForm';
import { NavAction } from 'ui/components/nav';
import { Button, DeleteItemModal } from 'ui/components/common';
import {
  PeopleIcon,
  DescriptionIcon,
  CalendarCheckIcon,
  BellIcon,
  GoogleMeetIcon,
  ZoomIcon,
  LocationIcon,
  TimeIcon,
  TagIcon,
} from 'ui/components/icon';
import { Avatar } from 'ui/components/img';
import { addClassNameToRichText, cn, copyToClipboard } from 'utils/helpers';
import c, { EVENT_TABS, SIZE } from 'utils/constants';
import getTypeColor from 'modules/account/calendar/utils/getTypeColor';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import { Guest, Tag as ITag } from 'utils/types';
import Tag from 'ui/components/common/Tag';
import { EventStatusButton } from 'modules/account/calendar/components';
import { useEventContext } from 'modules/account/calendar/utils/providers/EventContext';
import { useTimezone } from 'utils/providers/timezone.provider';

import useModal from '../Modal/useModal';
import { TOUR_SELECTOR } from '../Tour';

type ComposerEventBodyProps = {
  mode: 'popover' | 'modal';
};

const ComposerEventBody: FC<ComposerEventBodyProps> = () => {
  const { setIsEditMode, isEditMode } = useEventContext();
  const { setCurrentStep } = useTour();
  const { showModal } = useModal();
  const { ACTIONS, PERMISSION_KEYS } = c;
  const canUpdateEvent = useCanRolePermissions(
    ACTIONS.UPDATE,
    PERMISSION_KEYS.EVENTS,
  );
  const canDeleteEvent = useCanRolePermissions(
    ACTIONS.DELETE,
    PERMISSION_KEYS.EVENTS,
  );
  const { selectedEventId, setSelectedEventId } = useEventContext();
  const { data: subscription } = useGetSubscription();
  const { data: event } = useGetEvent(selectedEventId as string);
  const { mutate: updateEvent, isLoading } = useUpdateEvent();
  const { mutate: deleteEvent, isLoading: deleteLoading } = useDeleteEvent(
    event?.startAt,
  );
  const { timezone } = useTimezone({ source: 'local' });

  const organizer = useMemo(() => {
    if (event?.actor) {
      if (!event?.actor?.meta) return null;

      const actor = JSON.parse(event?.actor?.meta);

      return actor;
    }

    return event?.accountUserId;
  }, [event]);

  if (!event) {
    return <div />;
  }

  const handleDeleteEvent = () => {
    deleteEvent(selectedEventId as string, {
      onSuccess: () => setSelectedEventId?.(null),
    });
  };

  const navOptions = compact([
    canUpdateEvent && {
      text: 'Edit',
      key: 'edit',
      onClick: () => {
        setIsEditMode?.(true);
        setTimeout(() => setCurrentStep(6), 100);
      },
      'data-tut': TOUR_SELECTOR.TOUR.CALENDAR.CALENDAR.MANAGE.EDIT_BUTTON,
    },
    canDeleteEvent && {
      text: 'Cancel',
      key: 'cancel',
      onClick: () => {
        showModal({
          title: 'Event',
          content: (
            <DeleteItemModal
              text="event"
              onClickDelete={handleDeleteEvent}
              isLoading={deleteLoading}
              validation
            />
          ),
        });
      },
    },
  ]);

  const onSubmit = (
    { date, startAt, endAt, ...rest }: EVENT_FORM,
    { setSubmitting }: FormikHelpers<EVENT_FORM>,
  ) => {
    const d = dayjs(date).format('YYYY-MM-DD');

    const payload = {
      ...rest,
      startAt: payloadDayjs(
        `${d} ${startAt}`,
        `${c.DATE_FORMAT} HH:mm`,
        timezone,
      ),
      endAt: payloadDayjs(`${d} ${endAt}`, `${c.DATE_FORMAT} HH:mm`, timezone),
    };

    updateEvent(
      { id: selectedEventId as string, values: payload },
      {
        onSuccess: () => {
          setSelectedEventId?.(null);
          setIsEditMode?.(false);
        },
        onSettled: () => {
          setSubmitting(false);
        },
      },
    );
  };

  const guestNameParser = (guest: Guest) => {
    const firstName = guest?.firstName || '';
    const lastName = guest?.lastName || '';

    if (firstName || lastName) {
      return `${firstName} ${lastName} (${guest.emailAddress})`.trim();
    }
    return guest.emailAddress;
  };

  const isGoogleLink = event?.eventUrl?.includes('google');

  return isEditMode ? (
    <Formik
      initialValues={
        {
          title: event?.title,
          guests: event?.guests,
          date: new Date(event?.startAt),
          notifyBefore: event?.notifyBefore || 10,
          eventUrl: event?.eventUrl || '',
          startAt: dayjs(event?.startAt).format('HH:mm'),
          endAt: dayjs(event?.endAt).format('HH:mm'),
          content: event?.content,
          type: event?.type,
          location: event?.location,
          tagId: event?.tagId?.map((tag: ITag) => tag._id),
        } as EVENT_FORM
      }
      onSubmit={onSubmit}
      validationSchema={EVENT_SCHEMA}
    >
      {() => (
        <div className="px-[22px] py-3">
          <EventForm
            isCreated
            onCancel={() => setIsEditMode?.(false)}
            isLoading={isLoading}
          />
        </div>
      )}
    </Formik>
  ) : (
    <div className="py-3 px-[22px]">
      <div className="flex items-center justify-between">
        <div className="flex w-full items-center justify-between">
          <div className="flex gap-x-4">
            <div
              className={cn(
                'h-[18px] w-[18px] rounded-[3px]',
                getTypeColor(event?.type, EVENT_TABS.MONTH),
              )}
            />
            <div>
              <p className="text-sm font-semibold">{event?.title}</p>
            </div>
          </div>
          <EventStatusButton />
        </div>
        {!subscription?.isExpired && !!navOptions.length && (
          <NavAction
            className="h-5 w-5 text-secondary-base"
            label={<TbDotsVertical className="h-5 w-5 text-secondary-base" />}
            options={navOptions}
            optionsClassName="w-[100px] right-0"
            data-tut={TOUR_SELECTOR.TOUR.CALENDAR.CALENDAR.MANAGE.MANAGE}
          />
        )}
      </div>
      {event?.location && event?.type === 'EVENT' && (
        <div className="flex items-center gap-4 py-3">
          <LocationIcon className="h-5 w-5 text-content" />
          <p className="text-sm">{event?.location}</p>
        </div>
      )}
      <div className="flex py-3">
        <TimeIcon className="mr-4 h-5 w-5 text-content" />
        <div className="flex">
          <p className="text-sm">
            {dayjs(event?.startAt).format('dddd, MMMM D')} |{' '}
            {dayjs(event?.startAt).format('h:mma')}-{' '}
            {dayjs(event?.endAt).format('h:mma')}
          </p>
        </div>
      </div>
      {event?.eventUrl && (
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-x-4 py-3">
            <IoVideocam className="h-5 w-5 flex-shrink-0 text-content" />
            <Button
              icon={isGoogleLink ? GoogleMeetIcon : ZoomIcon}
              left
              label="JOIN THE MEETING"
              className="sm secondary flex-shrink-0 py-2"
              onClick={() => window.open(event?.eventUrl, '_blank')}
            />
            <p className="max-w-[260px] truncate text-content text-sm">
              {event?.eventUrl}
            </p>
            <button
              type="button"
              className="absolute right-12"
              onClick={() => copyToClipboard(event?.eventUrl)}
            >
              <MdContentCopy className="h-5 w-5 text-secondary-base" />
            </button>
          </div>
        </div>
      )}
      <div className="flex items-start gap-x-4 py-3">
        <PeopleIcon className="h-5 w-5 text-content" />
        <div>
          <p className="text-sm">{(event?.guests.length || 0) + 1} guests</p>
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-x-4">
              <Avatar user={organizer} size={SIZE.Small} />
              <div>
                <p className="text-sm">{organizer?.fullName}</p>
                <p className="text-content text-xs">Organizer</p>
              </div>
            </div>
            {event?.guests.map((guest: Guest) => (
              <div
                key={guest.emailAddress}
                className="flex max-w-[490px] items-center justify-between"
              >
                <div className="flex items-center gap-x-4">
                  <Avatar user={guest} size={SIZE.Small} />
                  <p className="text-sm">{guestNameParser(guest)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex py-3">
        <DescriptionIcon className="mr-4 h-5 w-5 flex-shrink-0 text-content" />
        <div
          className="break-all"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              addClassNameToRichText(
                'text-sm',
                event?.content ?? '<p>None</p>',
              ) as string,
            ),
          }}
        />
      </div>
      <div className="flex items-center py-3">
        <BellIcon className="mr-4 h-5 w-5 text-content" />
        <p className="text-sm">
          {event?.notifyBefore}{' '}
          {event?.notifyBefore === 60 ? 'hour' : 'minutes'} before
        </p>
      </div>
      <div className="flex items-center py-3">
        <CalendarCheckIcon className="mr-4 h-5 w-5 text-content" />
        <p className="text-sm">{organizer?.fullName}</p>
      </div>
      <div className="flex items-center py-3">
        <TagIcon className="mr-4 h-5 w-5 text-content" />
        <div className="flex flex-1 gap-0.5 flex-wrap">
          {event?.tagId?.length ? (
            event?.tagId?.map((tag: ITag) => (
              <Tag label={tag.tag} size={SIZE.Small} />
            ))
          ) : (
            <p className="text-sm">No tags</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComposerEventBody;
